import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { SelectOption } from '@dealroadshow/uikit/core/components/Select';
import { AsyncSelect } from '@/Framework/UI/Organisms/FinalForm/Fields/Select';
import { InputActionMeta } from 'react-select/dist/declarations/src/types';
import { Field, useField } from '@/Framework/UI/Organisms/FinalForm';
import { IFinalFormFieldInputComponentProps } from '@/Framework/UI/Organisms/FinalForm/interfaces';
import {
  PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY,
  UNKNOWN_BOND_OPTION,
  UNKNOWN_BOND_VALUE,
} from '../constants';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import {
  TSetPayload,
  useFormPayloadContext,
} from '@/finsight/application/PortfolioMonitor/FormPayloadContext';
import useGetPositions from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/common/useGetPositions';
import {
  mapBondsToOptions,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/heplers';
import {
  ICreatePortfolioFormState,
  IPortfolioBondLocal,
  IPosition,
} from '@/finsight/domain/vo/PortfolioMonitor/ICreatePortfolioFormState';
import { getSelectInputValue } from './helpers';

import spaces from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';
import styles from './styles.scss';

type TInput = IFinalFormFieldInputComponentProps<{ label: string, value: IPortfolioBondLocal, customBondName?: string }>['input'];

interface IProps {
  cellName: string,
  row: IPosition,
  rowIndex: number,
}

const DealNameSelect = ({ cellName, row, rowIndex }: IProps) => {
  const { listIndex } = useGetPositions();
  const { getMappedBondsOptions, setUnknownBond } = usePortfoliosContext();
  const { payload, setPayload } = useFormPayloadContext();
  const { input } = useField(`${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }`);
  const [isControlShouldRenderValue, setIsControlShouldRenderValue] = useState(true);

  const getOptions = (query: string) => {
    return getMappedBondsOptions(query).then((bonds) => {
      return [...mapBondsToOptions(bonds), UNKNOWN_BOND_OPTION];
    });
  };

  const loadOptions = (query, callback) => {
    getOptions(query).then((resp) => callback(resp));
  };

  const debounceLoadOptions = useCallback(debounce(loadOptions, 500), []);

  const updateBond = (value) => {
    setPayload((prevState) => ({
      ...prevState,
      lists: prevState.lists.map((list, idx) => {
        if (idx === listIndex) {
          return {
            ...list,
            positions: list.positions.map((pos) => (
                pos.localId === row.localId ? {
                  ...pos,
                  bond: {
                    ...value.value,
                    label: value.label,
                    customBondName: value.value?.dealId ? null : value.label,
                  },
                } : pos
            )),
          };
        }
        return list;
      }),
    }));
  };

  const handleInputBlurOrMenuClose = (value, input: TInput, metaData: InputActionMeta) => {
    if (metaData.action === 'input-blur' || metaData.action === 'menu-close') {
      onChange({
        value: input?.value.customBondName && !metaData.prevInputValue
            ? input?.value.customBondName : (metaData.prevInputValue || value || input?.value?.value),
        label: input?.value.customBondName && !metaData.prevInputValue
            ? input?.value.customBondName : (metaData.prevInputValue || value || input?.value?.value),
      });
    }
  };
  const handleInputChange = (
    value: string, metaData: InputActionMeta, listIndex: number, row, setPayload: TSetPayload,
  ) => {
    if (metaData.action === 'input-change') {
      setPayload((prevState) => {
        const indexofPosition = prevState.lists[listIndex].positions.findIndex((pos) => pos.localId === row.localId);
        if (indexofPosition >= 0) {
          let position = prevState.lists[listIndex].positions[indexofPosition];

          prevState.lists[listIndex].positions[indexofPosition] = {
            ...position,
            bond: {
              ...row.bond,
              dealId: null,
              trancheId: null,
              customBondName: value || metaData.prevInputValue,
            },
          };
        }

        return prevState;
      });
    }
  };

  const onChange = (value) => {
    if (value?.value === UNKNOWN_BOND_VALUE) {
        setUnknownBond(row);
        return;
      }

      if (value) {
        updateBond(value);
      }
  };

  const onInputChange = (
      value: string,
      metaData: InputActionMeta,
      input: TInput,
      listIndex: number,
      row,
      setPayload: TSetPayload,
  ) => {
    handleInputBlurOrMenuClose(value, input, metaData);
    handleInputChange(value, metaData, listIndex, row, setPayload);
  };

  const onInputChangeWrapper = useCallback(
    (value: string, metaData: InputActionMeta) => (
        onInputChange(value, metaData, input, listIndex, row, setPayload)
    ),
    [input, listIndex, row, setPayload, updateBond],
  );

  const CustomOption = (props) => (
    <SelectOption ownProps={ props } dataTest="option" />
  );

  const OptionWrapper = (props) => (
    <CustomOption { ...props }>
      {
        props.data.value === UNKNOWN_BOND_VALUE ? (
          <div style={ { color: '#4a76b6' } }>
            { props.children }
          </div>
        )
        :
        props.children
      }
    </CustomOption>
  );

  const onBlur = (input: TInput, payload: ICreatePortfolioFormState) => {
    const selectedOptionLabel = payload.lists[listIndex].positions
      .find((pos) => pos.localId === row.localId)?.bond?.customBondName;

    if (!selectedOptionLabel) {
      setIsControlShouldRenderValue(false);
    } else {
      setIsControlShouldRenderValue(true);
      input.onBlur();
    }
  };

  return (
    <Field
      name={ `${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
      dataTest={ `${ PORTFOLIO_REVIEW_UNKNOWNS_FIELD_ARRAY }[${ rowIndex }].${ cellName }` }
      component={ (fieldProps) => (
        // @ts-ignore
        <AsyncSelect
          { ...fieldProps }
          menuPlacement="auto"
          defaultOptions={ [UNKNOWN_BOND_OPTION] }
          loadOptions={ debounceLoadOptions }
          components={ { Option: OptionWrapper } }
          onInputChange={ onInputChangeWrapper }
          filterOption={ () => true }
          placeholder="Type a Bond name"
          isSupportHighlighting={ false }
          formFieldClassName={ cn(spaces.mbn, styles.select) }
          closeMenuOnSelect={ false }
          isClearable
          input={ {
            ...fieldProps.input,
            value: getSelectInputValue(fieldProps.input),
            onBlur: () => onBlur(fieldProps.input, payload),
            onChange: (value) => {
              if (value?.value !== UNKNOWN_BOND_VALUE) {
                fieldProps.input.onChange(value);
              }
              onChange(value);
            },
          } }
          controlShouldRenderValue={ isControlShouldRenderValue }
        />
      ) }
    />
  );
};

export default DealNameSelect;
